import { css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';
import type { FC } from 'react';
import { useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

import { AnalysisMenuHeader } from '@/features/analysisMenu/components/AnalysisMenuHeader';
import { AnalysisMenuItem } from '@/features/analysisMenu/components/AnalysisMenuItem';
import { AnalysisMenuItemSkeleton } from '@/features/analysisMenu/components/AnalysisMenuItemSkeleton';
import { AnalysisMenuLoadingState } from '@/features/analysisMenu/components/AnalysisMenuLoadingState';
import { useAnalysisMenuListData } from '@/features/analysisMenu/hooks/useAnalysisMenuListData';
import { useAnalysisMenuStore } from '@/features/analysisMenu/stores/analysis-menu.store';
import type { AnalysisItemData } from '@/features/analysisMenu/types';
import { useLastVisitedAnalysis } from '@/shared/hooks/analysis/useLastVisitedAnalysis';
import type { AnalysisStatus, AnalysisType } from '@/shared/types/analysis';

interface AnalysisMenuListProps {
  className?: string;
  type: AnalysisType;
  selectedAnalysisId?: string;
  analysesMax: number;
  noSubscriptionActive?: boolean;
  isLoading?: boolean;
  onCreateNew?: () => void;
  onAnalysisClick: (analysis: AnalysisItemData) => void;
  onRename?: (analysisId: string, newName: string) => void;
  onStop?: (analysisId: string) => void;
  onDelete?: (analysisId: string) => void;
  onAutoRedirect?: (analysisId: string) => void;
  handleExport?: () => Promise<void>;
  navigateToSubscription?: () => void;
}

export const AnalysisMenuList: FC<AnalysisMenuListProps> = ({
  className,
  type,
  selectedAnalysisId,
  analysesMax,
  noSubscriptionActive,
  isLoading: parentIsLoading,
  onCreateNew,
  onAnalysisClick,
  onRename,
  onStop,
  onDelete,
  onAutoRedirect,
  handleExport,
  navigateToSubscription,
}) => {
  // Récupération des données via le hook avec infinite query
  const {
    analyses,
    loading: dataIsLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAnalysisMenuListData(type);

  // Store pour l'état partagé
  const { isLoading, setIsLoading, setIsEmpty, setError, searchQuery, setSearchQuery } = useAnalysisMenuStore();

  // Hook pour la dernière analyse visitée
  const { getLastVisited, setLastVisited } = useLastVisitedAnalysis();

  // Synchroniser l'état du hook avec le store
  useEffect(() => {
    setIsLoading(parentIsLoading || dataIsLoading);
    setIsEmpty(analyses.length === 0);
    setError(error ? 'Erreur lors du chargement des analyses' : null);
    setSearchQuery(''); // Réinitialiser la recherche à chaque chargement
  }, [parentIsLoading, dataIsLoading, analyses.length, error, setIsLoading, setIsEmpty, setError, setSearchQuery]);

  // Auto-redirection vers la première analyse disponible
  useEffect(() => {
    if (!isLoading && analyses.length > 0 && !selectedAnalysisId && onAutoRedirect) {
      // Priorité 1: Dernière analyse visitée
      const lastVisited = getLastVisited(type);
      const lastVisitedAnalysis = analyses.find((analysis) => analysis.id === lastVisited);

      if (lastVisitedAnalysis) {
        onAutoRedirect(lastVisitedAnalysis.id);
        return;
      }

      // Priorité 2: Première analyse en cours
      const inProgressAnalyses = analyses.filter(
        (analysis) => analysis.status === 'starting' || analysis.status === 'active',
      );
      if (inProgressAnalyses.length > 0) {
        onAutoRedirect(inProgressAnalyses[0].id);
        return;
      }

      // Priorité 3: Première analyse terminée
      const completedAnalyses = analyses.filter(
        (analysis) => analysis.status === 'inactive' || analysis.status === 'error',
      );
      if (completedAnalyses.length > 0) {
        onAutoRedirect(completedAnalyses[0].id);
        return;
      }
    }
  }, [isLoading, analyses, selectedAnalysisId, onAutoRedirect, getLastVisited, type]);

  // Sauvegarder l'analyse sélectionnée
  useEffect(() => {
    if (selectedAnalysisId) {
      setLastVisited(type, selectedAnalysisId);
    }
  }, [selectedAnalysisId, type, setLastVisited]);

  const menuStyles = css({
    w: 'full',
    h: '100vh',
    bg: 'transparent',
    borderRight: '1px solid',
    borderColor: 'base.segBg',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    position: 'relative',
  });

  const contentStyles = stack({
    direction: 'column',
    flex: 1,
    overflow: 'hidden',
    p: 0,
  });

  const scrollAreaStyles = stack({
    direction: 'column',
    flex: 1,
    overflow: 'auto',
    px: '0.5625rem', // 9px
    gap: 0,
  });

  const sectionStyles = css({
    mb: 'xs', // 5px
  });

  const sectionTitleStyles = css({
    fontSize: 'xs', // 10px selon Figma
    fontWeight: 600,
    color: 'font.lowkey', // font-lowkey
    textTransform: 'uppercase',
    mb: 'xs', // 5px
  });

  const analysisListStyles = stack({
    direction: 'column',
  });

  const emptyStateStyles = css({
    textAlign: 'center',
    py: '2rem',
    color: 'font.lowkey', // font-lowkey
    fontSize: '2xs',
  });

  // Fonction pour filtrer les analyses par statut et query
  const filterAnalyses = (...status: AnalysisStatus[]) => {
    return analyses.filter(
      (analysis) => status.includes(analysis.status) && analysis.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  };

  const inProgressAnalyses = filterAnalyses('starting', 'active');
  const completedAnalyses = filterAnalyses('inactive');

  const maxAnalysesReached = !isLoading && inProgressAnalyses.length > 0 && inProgressAnalyses.length >= analysesMax;

  const handleAnalysisAction = (analysis: AnalysisItemData, action: 'rename' | 'stop' | 'delete', newName?: string) => {
    switch (action) {
      case 'rename':
        if (newName && onRename) {
          onRename(analysis.id, newName);
        }
        break;
      case 'stop':
        if (onStop) {
          onStop(analysis.id);
        }
        break;
      case 'delete':
        if (onDelete) {
          onDelete(analysis.id);
        }
        break;
    }
  };

  return (
    <div className={`${menuStyles} ${className || ''}`}>
      <AnalysisMenuHeader
        onSearchChange={setSearchQuery}
        searchQuery={searchQuery}
        showCreateButton={true}
        onCreateNew={onCreateNew}
        maxAnalysesReached={maxAnalysesReached}
        noSubscriptionActive={noSubscriptionActive}
        navigateToSubscription={navigateToSubscription}
        isLoading={isLoading}
        handleExport={handleExport}
      />

      <div className={contentStyles}>
        {isLoading ? (
          <AnalysisMenuLoadingState />
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            hasMore={hasNextPage}
            loader={
              <div key="loader">
                <AnalysisMenuItemSkeleton />
                <AnalysisMenuItemSkeleton />
              </div>
            }
            useWindow={true}
            element="div"
            className={scrollAreaStyles}
          >
            {/* Section EN COURS */}
            <div className={sectionStyles}>
              <div className={sectionTitleStyles}>
                En cours {inProgressAnalyses.length}/{analysesMax}
              </div>

              <div className={analysisListStyles}>
                {inProgressAnalyses.length > 0 ? (
                  inProgressAnalyses.map((analysis) => (
                    <AnalysisMenuItem
                      key={analysis.id}
                      analysis={analysis}
                      isSelected={selectedAnalysisId === analysis.id}
                      onClick={() => onAnalysisClick(analysis)}
                      onRename={(newName) => handleAnalysisAction(analysis, 'rename', newName)}
                      onStop={() => handleAnalysisAction(analysis, 'stop')}
                      onDelete={() => handleAnalysisAction(analysis, 'delete')}
                    />
                  ))
                ) : (
                  <div className={emptyStateStyles}>Aucune analyse en cours</div>
                )}
              </div>
            </div>

            {/* Section TERMINÉES */}
            <div className={`${sectionStyles} ${css({ mt: '2.1875rem' })}`}>
              <div className={sectionTitleStyles}>Terminées ({completedAnalyses.length})</div>

              <div className={analysisListStyles}>
                {completedAnalyses.length > 0 ? (
                  completedAnalyses.map((analysis) => (
                    <AnalysisMenuItem
                      key={analysis.id}
                      analysis={analysis}
                      enableMenu={true}
                      isSelected={selectedAnalysisId === analysis.id}
                      onClick={() => onAnalysisClick(analysis)}
                      onRename={(newName) => handleAnalysisAction(analysis, 'rename', newName)}
                      onDelete={() => handleAnalysisAction(analysis, 'delete')}
                    />
                  ))
                ) : (
                  <div className={emptyStateStyles}>Aucune analyse terminée</div>
                )}
              </div>
            </div>
          </InfiniteScroll>
        )}
      </div>
    </div>
  );
};
